// src/pages/Careers.jsx
import React, { useState } from "react";

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);

  const values = [
    {
      title: "🎯 Ownership over hierarchy",
      desc: "Every team member has real responsibility and genuine impact from day one.",
    },
    {
      title: "📚 Learning over comfort",
      desc: "We invest in skills, certifications, and continuous professional growth.",
    },
    {
      title: "💬 Clarity over politics",
      desc: "Straightforward, honest communication at every level.",
    },
    {
      title: "📈 Impact over activity",
      desc: "We measure results, not hours. Outcomes matter most.",
    },
  ];

  const jobs = [
    {
      id: 1,
      title: "AWS Cloud Architect",
      experience: "4+ Years",
      location: "Madurai, Tamil Nadu – Remote",

      overview:
        "We are looking for an experienced AWS Cloud Architect to design, deploy, and manage scalable, secure, and highly available infrastructure on AWS.",

      responsibilities: [
        "Design scalable, secure AWS architectures",
        "Manage EC2, S3, RDS (MySQL), VPC, CloudFront, Route53, IAM",
        "Handle application and infrastructure deployments",
        "Deploy Docker-based applications on EC2",
        "Build CI/CD pipelines",
        "Develop automation scripts using Python",
        "Manage AWS RDS MySQL databases",
        "Configure CloudFront CDN",
        "Implement security best practices",
        "Set up monitoring using CloudWatch",
        "Optimize infrastructure cost",
      ],

      skills: [
        "AWS (EC2, S3, RDS, VPC, CloudFront, IAM)",
        "Docker",
        "CI/CD pipelines",
        "Python",
        "MySQL",
      ],

      cost: [
        "Analyze and optimize AWS cost",
        "Use Reserved, Auto Scaling, Spot Instances",
      ],

      collaboration: [
        "Work with development teams",
        "Guide cloud architecture decisions",
      ],

      preferred: [
        "Production cloud experience",
        "High-traffic applications",
        "Security & performance optimization",
      ],

      important:
        "Candidates must have hands-on experience managing production AWS environments and deployments on EC2.",
    },
  ];

  const handleSendCV = () => {
    const url =
      "https://mail.google.com/mail/?view=cm&fs=1&to=administrator@tickbox.co.in&su=Application%20for%20Careers%20at%20TickBox&body=Hi%20Team,%0A%0AI%20am%20interested%20in%20joining%20TickBox.%0A%0AThanks";
    window.open(url, "_blank");
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">

        {/* HEADER */}
        <div className="text-center mb-14">
          <p className="text-[#d97925] uppercase tracking-widest text-sm font-semibold">
            Careers
          </p>
          <h2 className="text-4xl font-bold text-black mt-4">
            Life at TickBox
          </h2>
          <p className="text-zinc-600 mt-4 text-sm max-w-xl mx-auto">
            Join a team focused on building impactful compliance technology
            across India.
          </p>
        </div>

        {/* VALUES */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-16">
          {values.map((item, i) => (
            <div
              key={i}
              className="border-l-4 border-[#d97925] p-6 rounded-xl shadow-sm hover:shadow-md transition"
            >
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-zinc-600">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* JOB LIST */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-10">
            Available Job Vacancies
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="group border rounded-2xl p-6 hover:shadow-xl transition"
              >
                <h4 className="text-xl font-semibold text-black group-hover:text-[#d97925]">
                  {job.title}
                </h4>

                <div className="flex gap-3 mt-2 text-xs text-zinc-500">
                  <span className="bg-zinc-100 px-2 py-1 rounded">
                    {job.experience}
                  </span>
                  <span className="bg-zinc-100 px-2 py-1 rounded">
                    {job.location}
                  </span>
                </div>

                <p className="text-sm text-zinc-600 mt-4 line-clamp-3">
                  {job.overview}
                </p>

                <button
                  onClick={() => setSelectedJob(job)}
                  className="mt-5 text-[#d97925] font-semibold hover:underline"
                >
                  View Full Job →
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-zinc-500 text-sm mb-5">
            Interested in joining us? Send your CV.
          </p>

          <button
            onClick={handleSendCV}
            className="px-6 py-3 bg-[#d97925] text-white rounded-xl hover:bg-orange-600 transition"
          >
            Send Your CV →
          </button>
        </div>

      </div>

      {/* MODAL */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-6">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl p-8 relative shadow-2xl">

            {/* CLOSE */}
            <button
              onClick={() => setSelectedJob(null)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-black text-xl"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold mb-2">
              {selectedJob.title}
            </h2>

            <p className="text-sm text-zinc-500 mb-6">
              {selectedJob.experience} • {selectedJob.location}
            </p>

            <p className="mb-6 text-zinc-700">
              {selectedJob.overview}
            </p>

            <Section title="Key Responsibilities" items={selectedJob.responsibilities} />
            <Section title="Required Skills" items={selectedJob.skills} />
            <Section title="Cost Optimization" items={selectedJob.cost} />
            <Section title="Collaboration" items={selectedJob.collaboration} />
            <Section title="Preferred Experience" items={selectedJob.preferred} />

            <div className="mt-6">
              <h4 className="font-semibold mb-2 text-red-500">
                Important Requirement
              </h4>
              <p className="text-sm text-zinc-700">
                {selectedJob.important}
              </p>
            </div>

            <button
              onClick={handleSendCV}
              className="mt-8 w-full py-3 bg-[#d97925] text-white rounded-xl font-semibold hover:bg-orange-600"
            >
              Apply for this Role →
            </button>

          </div>
        </div>
      )}
    </section>
  );
};

/* REUSABLE SECTION */
const Section = ({ title, items }) => (
  <div className="mb-6">
    <h4 className="font-semibold mb-2">{title}</h4>
    <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-700">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  </div>
);

export default Careers;